"use client";

import { useEffect, useRef, useState } from "react";
import { createGamSlotId } from "@shrimpster/ad-services-publisher-web/gam";
import { resolveSlotCreativeTheme } from "@/lib/ads/creativeTheme.mjs";
import { useAds } from "./AdsProvider";

const COLLAPSED_STATES = new Set(["empty", "error", "timeout", "disposed", "ineligible"]);

export default function FeedAdSlot({ opportunity }) {
  const {
    enabled,
    plan,
    runtime,
    environment,
    siteId,
    creativeTheme,
    consent,
    requestPolicy,
    housePromotionEnabled,
    resolveHouseTargeting,
    sourceRevision,
  } = useAds();
  const { opportunityId, placementId, sequence } = opportunity;
  const sentinelRef = useRef(null);
  const handleRef = useRef(null);
  const [nearViewport, setNearViewport] = useState(false);
  const [state, setState] = useState("waiting");
  const [houseTargeting, setHouseTargeting] = useState(undefined);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !enabled) return undefined;
    if (typeof IntersectionObserver === "undefined") {
      setNearViewport(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setNearViewport(true);
          observer.disconnect();
        }
      },
      { rootMargin: "1200px 0px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [enabled]);

  useEffect(() => {
    if (!nearViewport || !enabled) return undefined;
    if (!housePromotionEnabled) {
      setHouseTargeting(null);
      return undefined;
    }
    let active = true;
    resolveHouseTargeting({ opportunityId })
      .then((targeting) => {
        if (active) setHouseTargeting(targeting || null);
      });
    return () => {
      active = false;
    };
  }, [enabled, housePromotionEnabled, nearViewport, opportunityId, resolveHouseTargeting]);

  const containerId = plan.kind === "provider-slot"
    ? createGamSlotId({
        bindingId: plan.bindingId,
        opportunityId,
      })
    : "";

  useEffect(() => {
    if (
      !nearViewport ||
      !enabled ||
      !runtime ||
      plan.kind !== "provider-slot" ||
      houseTargeting === undefined
    ) return undefined;
    const backgroundColor = window.getComputedStyle(document.documentElement)
      .getPropertyValue("--bg")
      .trim();
    const slotCreativeTheme = resolveSlotCreativeTheme({
      publisherTheme: creativeTheme,
      backgroundColor,
    });
    if (!slotCreativeTheme) {
      console.warn("[ads] could not resolve the host theme for the GAM slot");
      return undefined;
    }
    const handle = runtime.requestSlot({
      binding: plan.binding,
      opportunityId,
      siteId,
      environment,
      placementId,
      creativeTheme: slotCreativeTheme,
      houseTargeting,
      containerId,
      consent,
      requestPolicy,
      onStateChange: ({ state: nextState, reason }) => {
        setState(nextState);
        if (nextState === "error") console.warn("[ads] GAM slot failed", reason || "unknown");
        if (COLLAPSED_STATES.has(nextState) && nextState !== "error" && nextState !== "disposed") {
          console.debug(`[ads] GAM opportunity ${sequence + 1} ${nextState}`);
        }
      },
    });
    handleRef.current = handle;
    return () => {
      handle.dispose();
      handleRef.current = null;
    };
  }, [consent, containerId, creativeTheme, enabled, environment, houseTargeting, nearViewport, opportunityId, placementId, plan, requestPolicy, runtime, sequence, siteId]);

  useEffect(() => {
    if (COLLAPSED_STATES.has(state)) handleRef.current?.dispose();
  }, [state]);

  if (!enabled || COLLAPSED_STATES.has(state)) return null;

  return (
    <div className="edge-mobile" data-ad-opportunity={sequence + 1}>
      <div
        ref={sentinelRef}
        className={nearViewport ? "relative min-h-[280px] sm:min-h-[320px]" : "h-px"}
        data-ad-runtime-revision={sourceRevision || undefined}
      >
        {nearViewport ? (
          <>
            {state === "waiting" || state === "requesting" ? <AdLoadingSkeleton /> : null}
            <div id={containerId} className="relative z-[1] w-full" />
          </>
        ) : null}
      </div>
    </div>
  );
}

function AdLoadingSkeleton() {
  return (
    <div
      className="absolute inset-0 animate-pulse rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface)] p-4"
      role="status"
      aria-label="Loading sponsored content"
    >
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-[var(--radius)] bg-[var(--border)]" />
        <div className="flex-1 space-y-2">
          <div className="h-3 w-32 rounded bg-[var(--border)]" />
          <div className="h-2.5 w-20 rounded bg-[var(--border)]" />
        </div>
      </div>
      <div className="mt-4 h-3 w-3/4 rounded bg-[var(--border)]" />
      <div className="mt-2 h-3 w-1/2 rounded bg-[var(--border)]" />
      <div className="mt-4 h-36 rounded-[var(--radius)] bg-[var(--border)] sm:h-40" />
    </div>
  );
}
